import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SparklesIcon,
  ChartBarIcon,
  PhoneIcon,
  UsersIcon,
  FlagIcon,
  FireIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowUpTrayIcon,
  CheckBadgeIcon,
  RocketLaunchIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import PageShell from '../../components/PageShell';
import CreditBalance from '../../components/CreditBalance';
import OnboardingChecklist from '../../components/OnboardingChecklist';
import { useChecklistSignals } from '../../components/OnboardingChecklist/useChecklistSignals';
import {
  TemperaturePill,
  DispositionBadge,
  GoalOutcomeIcon,
  TemperatureDistributionBar,
} from '../../components/intelligence';
import { DISPOSITION_GROUPS } from '../../components/intelligence/DispositionBadge';
import { useDashboardPage } from './useDashboardPage';
import { callsAPI } from '../../services/api';
import { Button, Card, Pill } from '../../ui';
import { APP_NAME } from '../../config/region';

// LT-309 (LT-285 C2): Dashboard rebuilt on DS v1 tokens. Retired the
// gradient welcome-header, gradient metric cards, and gradient CTA buttons.
// Cards now use <Card> (solid surface-1). Buttons use <Button> with DS intents.
// Layout preserved; all analytics events + data loading unchanged.

function getPlanDisplayName(plan: string, planNamesById: Record<string, string>): string {
  if (!plan || plan === 'free') return 'Trial';
  const name = planNamesById[plan.toLowerCase()];
  if (name) return name;
  return plan.charAt(0).toUpperCase() + plan.slice(1).replace(/_/g, ' ');
}

const DashboardPage: React.FC = () => {
  const {
    user,
    subscriptionInfo,
    planNamesById,
    isUserNew,
    intelligence,
    analyticsSummary,
    intelligenceLoading,
  } = useDashboardPage();
  const navigate = useNavigate();
  const [hiddenLeadIds, setHiddenLeadIds] = React.useState<Set<string>>(new Set());
  const [updatingCallSummaryId, setUpdatingCallSummaryId] = React.useState<string | null>(null);
  // LT-314 — first-run checklist lives on the dashboard for fresh users.
  // Self-hides once all tasks are complete OR the user dismisses it
  // (persisted per-user in localStorage).
  const checklistSignals = useChecklistSignals();

  const totalCalls = intelligence?.total_calls ?? analyticsSummary?.total_calls ?? 0;
  const leadsContacted =
    intelligence?.total_leads_contacted ?? analyticsSummary?.successful_calls ?? 0;
  const goalRate = intelligence?.goal_achievement_rate ?? 0;
  const visibleNeedsAttention = (intelligence?.needs_attention ?? []).filter(
    (lead) => !hiddenLeadIds.has(lead.id),
  );
  const pendingFollowUpsCount = Math.max(
    0,
    (intelligence?.follow_ups_pending ?? 0) - hiddenLeadIds.size,
  );

  const tempBreakdown = intelligence?.temperature_breakdown;
  const tempEntryCount = tempBreakdown ? Object.keys(tempBreakdown).length : 0;
  const dispBreakdown = intelligence?.disposition_breakdown;
  const dispEntryCount = dispBreakdown ? Object.keys(dispBreakdown).length : 0;

  return (
    <PageShell>
      {/* Welcome header — solid, DS-tokened. No gradient. */}
      <header className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <span className="inline-flex items-center justify-center h-7 w-7 rounded-ds-lg bg-accent-tint border border-accent-border">
            <SparklesIcon className="h-4 w-4 text-accent-text" />
          </span>
          <h1 className="text-lg sm:text-xl font-semibold text-slate-50 tracking-tight">
            {isUserNew
              ? `Welcome to ${APP_NAME}, ${user?.first_name || user?.email}!`
              : `Welcome back, ${user?.first_name || user?.email}!`}
          </h1>
        </div>
        <p className="text-sm text-slate-400">
          {isUserNew
            ? "Let's get you started with your first campaign!"
            : 'Your intelligence hub — overview of all campaigns'}
        </p>
      </header>

      <OnboardingChecklist userId={user?.id ?? null} signals={checklistSignals} />

      {/* Quick actions */}
      <div className="flex items-center gap-2 sm:gap-3 mb-6 flex-wrap">
        <Button intent="primary" onClick={() => navigate('/campaigns/create')}>
          <RocketLaunchIcon className="h-4 w-4" />
          New Campaign
        </Button>
        <Button intent="secondary" onClick={() => navigate('/contacts')}>
          <ArrowUpTrayIcon className="h-4 w-4" />
          Upload Contacts
        </Button>
        {pendingFollowUpsCount > 0 && (
          <Button intent="ghost" onClick={() => navigate('/follow-ups')}>
            <FlagIcon className="h-4 w-4 text-warn" />
            Review follow-ups
            <Pill intent="warn">{pendingFollowUpsCount}</Pill>
          </Button>
        )}
      </div>

      {/* Metric cards — 6-column grid preserved */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <div>
          <CreditBalance compact={true} />
        </div>
        <MetricCard
          icon={ChartBarIcon}
          label="Subscription"
          value={
            subscriptionInfo
              ? getPlanDisplayName(subscriptionInfo.plan, planNamesById)
              : '...'
          }
          sub={subscriptionInfo?.status === 'active' ? 'Active' : 'Inactive'}
          subIntent={subscriptionInfo?.status === 'active' ? 'ok' : 'neutral'}
        />
        <MetricCard
          icon={PhoneIcon}
          label="Total Calls"
          value={intelligenceLoading ? '...' : String(totalCalls)}
        />
        <MetricCard
          icon={UsersIcon}
          label="Contacts Reached"
          value={intelligenceLoading ? '...' : String(leadsContacted)}
        />
        <MetricCard
          icon={CheckBadgeIcon}
          label="Goal Rate"
          value={intelligenceLoading ? '...' : goalRate > 0 ? `${goalRate}%` : '—'}
          sub={goalRate > 0 ? 'of goals met' : undefined}
          subIntent="ok"
          attention={goalRate > 0 && goalRate < 30}
        />
        <MetricCard
          icon={FlagIcon}
          label="Follow-ups"
          value={intelligenceLoading ? '...' : String(pendingFollowUpsCount)}
          attention={pendingFollowUpsCount > 0}
        />
      </div>

      {/* Distribution widgets */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card>
          <div className="flex items-center gap-2 mb-3">
            <FireIcon className="h-4 w-4 text-accent-text" />
            <h2 className="text-sm font-semibold text-slate-50">Contact Temperature</h2>
          </div>
          {intelligenceLoading ? (
            <div className="h-16 flex items-center justify-center text-sm text-slate-400">
              Loading...
            </div>
          ) : tempBreakdown && tempEntryCount > 0 ? (
            tempEntryCount === 1 ? (
              <SingleValueDisplay breakdown={tempBreakdown} type="temperature" />
            ) : (
              <TemperatureDistributionBar breakdown={tempBreakdown} />
            )
          ) : (
            <p className="text-sm text-slate-400 text-center py-4">
              No temperature data yet
            </p>
          )}
        </Card>

        <Card>
          <div className="flex items-center gap-2 mb-3">
            <ChartBarIcon className="h-4 w-4 text-accent-text" />
            <h2 className="text-sm font-semibold text-slate-50">Disposition Breakdown</h2>
          </div>
          {intelligenceLoading ? (
            <div className="h-16 flex items-center justify-center text-sm text-slate-400">
              Loading...
            </div>
          ) : dispBreakdown && dispEntryCount > 0 ? (
            dispEntryCount === 1 ? (
              <SingleValueDisplay breakdown={dispBreakdown} type="disposition" />
            ) : (
              <DispositionDistributionBar breakdown={dispBreakdown} />
            )
          ) : (
            <p className="text-sm text-slate-400 text-center py-4">
              No disposition data yet
            </p>
          )}
        </Card>
      </div>

      {/* Activity + Follow-ups */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card padding="tight">
          <div className="flex items-center gap-2 mb-3 px-1">
            <ClockIcon className="h-4 w-4 text-accent-text" />
            <h2 className="text-sm font-semibold text-slate-50">Recent Activity</h2>
          </div>
          <p className="text-xs text-slate-400 mb-3 px-1">
            Latest calls across all campaigns
          </p>
          <div className="divide-y divide-white/[0.06]">
            {intelligenceLoading ? (
              <div className="p-4 text-center text-sm text-slate-400">Loading...</div>
            ) : intelligence?.recent_calls && intelligence.recent_calls.length > 0 ? (
              intelligence.recent_calls.map((call) => {
                const callTime = call.timestamp ? new Date(call.timestamp) : null;
                const dateStr = callTime ? callTime.toLocaleDateString() : '';
                const timeStr = callTime
                  ? callTime.toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    })
                  : '';
                return (
                  <button
                    key={call.id}
                    type="button"
                    className="w-full text-left px-2 py-2.5 hover:bg-white/[0.04] rounded-ds-md transition-colors overflow-hidden"
                    onClick={() => {
                      if (call.run_id) navigate(`/runs/${call.run_id}/calls/${call.id}`);
                    }}
                  >
                    <div className="flex items-center justify-between mb-1 min-w-0">
                      <span className="text-sm text-slate-50 font-medium truncate mr-2">
                        {call.lead_name}
                      </span>
                      <span className="text-xs text-slate-400 whitespace-nowrap flex-shrink-0">
                        {dateStr}
                        {timeStr ? `, ${timeStr}` : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <TemperaturePill temperature={call.lead_temperature} />
                      <DispositionBadge disposition={call.disposition} />
                      <GoalOutcomeIcon outcome={call.goal_outcome} showLabel />
                      {call.follow_up_action?.action_required && (
                        <Pill intent="warn">
                          <FlagIcon className="h-3 w-3" />
                          Follow-up
                        </Pill>
                      )}
                    </div>
                    {call.analysis_summary && (
                      <p className="text-xs text-slate-300 line-clamp-1 break-words overflow-hidden">
                        {call.analysis_summary}
                      </p>
                    )}
                  </button>
                );
              })
            ) : (
              <div className="p-6 text-center">
                <PhoneIcon className="h-8 w-8 text-slate-500 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No calls yet</p>
              </div>
            )}
          </div>
        </Card>

        <Card padding="tight" id="follow-ups">
          <div className="flex items-center justify-between gap-2 mb-3 px-1">
            <div className="flex items-center gap-2">
              <ExclamationTriangleIcon className="h-4 w-4 text-warn" />
              <h2 className="text-sm font-semibold text-slate-50">Follow-ups</h2>
            </div>
            <button
              type="button"
              onClick={() => navigate('/follow-ups')}
              className="inline-flex items-center gap-1 text-xs font-medium text-accent-text hover:text-accent transition-colors"
            >
              View all
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
          <p className="text-xs text-slate-400 mb-3 px-1">
            Contacts with pending follow-ups or high interest
          </p>
          <div className="divide-y divide-white/[0.06]">
            {intelligenceLoading ? (
              <div className="p-4 text-center text-sm text-slate-400">Loading...</div>
            ) : visibleNeedsAttention.length > 0 ? (
              visibleNeedsAttention.map((lead) => (
                <div
                  key={lead.id}
                  className="w-full text-left px-2 py-2.5 hover:bg-white/[0.04] rounded-ds-md transition-colors"
                >
                  <div className="flex items-center justify-between mb-1">
                    <button
                      type="button"
                      onClick={() => navigate(`/contacts/${lead.id}`)}
                      className="text-sm text-slate-50 font-medium truncate mr-2 hover:text-accent-text transition-colors"
                    >
                      {lead.name}
                    </button>
                    <div className="flex items-center gap-1.5">
                      <TemperaturePill temperature={lead.current_temperature} />
                      {lead.has_pending_follow_up && (
                        <span
                          className="w-2 h-2 rounded-full bg-warn animate-pulse"
                          title="Pending follow-up"
                        />
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-400">
                    <span>{lead.phone}</span>
                    <span>
                      {lead.total_attempts} attempt
                      {lead.total_attempts !== 1 ? 's' : ''}
                    </span>
                    {lead.last_contact_at && (
                      <span>
                        Last: {new Date(lead.last_contact_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  {lead.follow_up_description && (
                    <p className="text-xs text-warn-text mt-1 line-clamp-1">
                      {lead.follow_up_description}
                    </p>
                  )}
                  {lead.has_pending_follow_up && lead.call_summary_id && (
                    <div className="mt-2 flex items-center gap-2">
                      <Button
                        intent="ghost"
                        size="sm"
                        onClick={async () => {
                          if (!window.confirm('Complete this follow-up?')) return;
                          try {
                            setUpdatingCallSummaryId(lead.call_summary_id!);
                            await callsAPI.updateFollowUpStatus(
                              lead.call_summary_id!,
                              'completed',
                            );
                            setHiddenLeadIds((prev) => {
                              const next = new Set(prev);
                              next.add(lead.id);
                              return next;
                            });
                          } catch {
                            window.alert(
                              'Failed to update follow-up status. Please try again.',
                            );
                          } finally {
                            setUpdatingCallSummaryId((prev) =>
                              prev === lead.call_summary_id ? null : prev,
                            );
                          }
                        }}
                        disabled={updatingCallSummaryId === lead.call_summary_id}
                        className="border border-ok-border text-ok-text hover:bg-ok-tint"
                      >
                        Complete
                      </Button>
                      <Button
                        intent="ghost"
                        size="sm"
                        onClick={async () => {
                          if (!window.confirm('Mark this follow-up as not required?'))
                            return;
                          try {
                            setUpdatingCallSummaryId(lead.call_summary_id!);
                            await callsAPI.updateFollowUpStatus(
                              lead.call_summary_id!,
                              'not_required',
                            );
                            setHiddenLeadIds((prev) => {
                              const next = new Set(prev);
                              next.add(lead.id);
                              return next;
                            });
                          } catch {
                            window.alert(
                              'Failed to update follow-up status. Please try again.',
                            );
                          } finally {
                            setUpdatingCallSummaryId((prev) =>
                              prev === lead.call_summary_id ? null : prev,
                            );
                          }
                        }}
                        disabled={updatingCallSummaryId === lead.call_summary_id}
                      >
                        Not required
                      </Button>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="p-6 text-center">
                <ExclamationTriangleIcon className="h-8 w-8 text-slate-500 mx-auto mb-2" />
                <p className="text-sm text-slate-400">
                  No contacts need attention right now
                </p>
              </div>
            )}
          </div>
        </Card>
      </div>
    </PageShell>
  );
};

// DS-tokened metric card. No gradient — solid accent-tint chip for the icon.
function MetricCard({
  icon: Icon,
  label,
  value,
  sub,
  subIntent,
  attention,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  sub?: string;
  subIntent?: 'ok' | 'warn' | 'neutral';
  attention?: boolean;
}) {
  const subClass =
    subIntent === 'ok'
      ? 'text-ok-text'
      : subIntent === 'warn'
        ? 'text-warn-text'
        : 'text-slate-400';
  return (
    <Card
      padding="tight"
      className={attention ? 'border-warn-border' : undefined}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="inline-flex items-center justify-center h-6 w-6 rounded-ds-md bg-accent-tint border border-accent-border">
          <Icon className="h-3.5 w-3.5 text-accent-text" />
        </span>
        <h3 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
          {label}
        </h3>
      </div>
      <p
        className={`text-sm sm:text-lg font-bold mb-1 ${
          attention ? 'text-warn-text' : 'text-slate-50'
        }`}
      >
        {value}
      </p>
      {sub && (
        <div className="flex items-center text-xs">
          <span className={subClass}>{sub}</span>
        </div>
      )}
    </Card>
  );
}

const TEMP_COLORS: Record<string, { dot: string; text: string; label: string }> = {
  hot: { dot: 'bg-red-400', text: 'text-red-400', label: 'Hot' },
  warm: { dot: 'bg-orange-400', text: 'text-orange-400', label: 'Warm' },
  lukewarm: { dot: 'bg-yellow-400', text: 'text-yellow-400', label: 'Lukewarm' },
  cold: { dot: 'bg-blue-400', text: 'text-blue-400', label: 'Cold' },
  dead: { dot: 'bg-slate-400', text: 'text-slate-400', label: 'Dead' },
};

function SingleValueDisplay({
  breakdown,
  type,
}: {
  breakdown: Record<string, number>;
  type: 'temperature' | 'disposition';
}) {
  const entries = Object.entries(breakdown).filter(([, v]) => v > 0);
  if (entries.length === 0) return null;

  if (type === 'temperature') {
    return (
      <div className="flex items-center gap-3 py-2">
        {entries.map(([key, count]) => {
          const cfg = TEMP_COLORS[key] || {
            dot: 'bg-slate-400',
            text: 'text-slate-300',
            label: key,
          };
          return (
            <div key={key} className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${cfg.dot}`} />
              <span className={`text-sm font-medium ${cfg.text}`}>{cfg.label}</span>
              <span className="text-lg font-bold text-slate-50 tabular-nums">
                {count}
              </span>
              <span className="text-xs text-slate-400">
                contact{count !== 1 ? 's' : ''}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  const groupTotals: { group: string; count: number; color: string }[] = [];
  for (const [groupName, config] of Object.entries(DISPOSITION_GROUPS)) {
    const count = config.dispositions.reduce(
      (sum, d) => sum + (breakdown[d] ?? 0),
      0,
    );
    if (count > 0) {
      groupTotals.push({ group: groupName, count, color: config.color });
    }
  }

  return (
    <div className="flex items-center gap-3 py-2 flex-wrap">
      {groupTotals.map((g) => (
        <div key={g.group} className="flex items-center gap-2">
          <span className={`w-3 h-3 rounded-full ${g.color}`} />
          <span className="text-sm font-medium text-slate-200">{g.group}</span>
          <span className="text-lg font-bold text-slate-50 tabular-nums">
            {g.count}
          </span>
          <span className="text-xs text-slate-400">
            call{g.count !== 1 ? 's' : ''}
          </span>
        </div>
      ))}
    </div>
  );
}

function DispositionDistributionBar({
  breakdown,
}: {
  breakdown: Record<string, number>;
}) {
  const groupTotals: { group: string; count: number; color: string }[] = [];
  for (const [groupName, config] of Object.entries(DISPOSITION_GROUPS)) {
    const count = config.dispositions.reduce(
      (sum, d) => sum + (breakdown[d] ?? 0),
      0,
    );
    if (count > 0) {
      groupTotals.push({ group: groupName, count, color: config.color });
    }
  }
  const total = groupTotals.reduce((sum, g) => sum + g.count, 0);
  if (total === 0)
    return <p className="text-sm text-slate-400 text-center py-4">No data</p>;

  return (
    <div>
      <div className="flex rounded-full h-3 overflow-hidden bg-surface-2">
        {groupTotals.map((g) => (
          <div
            key={g.group}
            className={`${g.color} transition-all duration-500`}
            style={{ width: `${(g.count / total) * 100}%` }}
            title={`${g.group}: ${g.count}`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
        {groupTotals.map((g) => (
          <div key={g.group} className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${g.color}`} />
            <span className="text-xs text-slate-300">{g.group}</span>
            <span className="text-xs text-slate-400 font-medium tabular-nums">
              {g.count}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default DashboardPage;
